<template>
	<div :class="!state.isOTPSent ? 'bg-1' : 'bg-2'">
	<Header :bg-class="state.isOTPSent ? 'bg-brand-blue' : 'bg-[#2C6339]'"/>
	<div class="home">
		<div class="w-full">
			<img
			v-if="state.isOTPSent"
				src="@/assets/home-hero.png"
				alt="home hero"
				class="w-full mx-auto mb-12"
			/>
			<img
			v-else
				src="@/assets/home-hero-2.png"
				alt="home hero"
				class="w-full mx-auto mb-12"
			/>
			<div class="relative z-10 px-4 rounded-lg bg-[#2c6239] py-7" :class="{'-mt-20' : !state.isOTPSent}">
				<div class="mb-8" :class="{ 'opacity-50': state.isOTPSent }">
					<label for="name" class="block mb-1 text-lg text-white"
						>Enter Your Mobile Number</label
					>
					<input
						type="tel"
						v-model.trim="mobileForm.fields.mobile"
						id="mobile"
						name="mobile"
						class="w-full p-3 bg-white rounded-md outline-none"
						placeholder="Mobile"
						maxlength="10"
						@blur="validateMobileField('mobile')"
						:class="state.isOTPSent ? 'bg-gray-400' : 'bg-brand'"
						:disabled="state.isOTPSent"
					/>
					<Error class="mt-2" :errors="mobileForm.errors.mobile" />
				</div>
				<div v-if="!state.isOTPSent">
					<button
						type="submit"
						@click="handleOtpSend"
						class="w-full py-3 mt-4 font-serif text-2xl text-center text-black rounded-lg outline-none bg-brand-yellow btn"
						:class="{'btn--loading': state.inProgress}"
						:disabled="state.inProgress"
					>
					<span>
						Send OTP
					</span>
					</button>
				</div>
				<template v-else>
					<div>
						<label for="otp" class="block mb-1 text-lg text-white"
							>Enter 4 digit OTP</label
						>
						<input
							id="otp"
							name="otp"
							class="w-full p-3 bg-white rounded-md outline-none"
							placeholder="4 digit OTP"
							maxlength="4"
							@blur="validateOTPField('otp')"
							v-model.trim="otpForm.fields.otp"
							@input="(val) => (otpForm.fields.otp = otpForm.fields.otp.toUpperCase())"
						/>
						<Error class="mt-2" :errors="otpForm.errors.otp" />
					</div>
					<div v-if="state.isOTPResent" class="my-2 text-sm text-white">
						OTP Resent to {{ mobileForm.fields.mobile }}
					</div>
					<div v-else class="my-2">
						<div v-if="state.canResendOTP" class="flex items-center">
							<span class="mr-2 text-sm text-white">Didn't recieve?</span>
							<button
								@click="resendOTP($event)"
								class="text-sm text-center text-white underline cursor-pointer"
							>
								Resend OTP
							</button>
						</div>
						<div v-else class="my-2 text-white">
							Resend OTP in {{ clock.timer.minutes }}:{{ clock.timer.seconds }}
						</div>
					</div>
					<div>
						<button
							type="submit"
							class="w-full py-3 mt-4 font-serif text-2xl text-center text-black rounded-lg outline-none bg-brand-yellow btn"
							@click.prevent="handleVerifyOTP"
							:class="{'btn--loading': state.inProgress}"
						:disabled="state.inProgress"
						>
						<span>
							Verify OTP
						</span>
						</button>
					</div>
				</template>
			</div>
		</div>
	</div>
</div>
</template>

<script setup>
import { onMounted, reactive, computed, ref, watch } from 'vue'
import { generateOTP, mobileLogin, verifyOTP } from '@/api'
import useValidateForm from '@/hooks/useValidateForm'
import Error from '@/components/Error.vue'
import useTimer from '@/hooks/useTimer'
import { moveToNextRoute } from '@/hooks/useNextRoute'
import useStatusCheck from '@/hooks/useStatusCheck'
import { useStore } from 'vuex'
import Header from '@/components/Header.vue'
import useEventCheck from "@/hooks/useEventCheck";

const store = useStore()
useEventCheck();

const state = reactive({
	inProgress: false,
	canResendOTP: false,
	isOTPSent: false,
	isOTPResent: false,
})
const { isLoggedIn } = useStatusCheck()
const { startTimer, clock } = useTimer()
const mobileForm = reactive({
	fields: {
		mobile: null,
	},
	errors: {
		mobile: [],
	},
	validations: {
		mobile: [
			{ name: 'required', message: 'Please enter your mobile' },
			{ name: 'mobile', message: 'Please enter a valid 10 digit mobile' },
		],
	},
})

const otpForm = reactive({
	fields: {
		otp: '',
	},
	errors: {
		otp: [],
	},
	validations: {
		otp: [
			{ name: 'required', message: 'Please enter OTP' },
			{
				name: 'minLength',
				minLength: 4,
				message: 'Please enter a valid 4 digit mobile',
			},
		],
	},
})

const { validateForm: validateMobileForm, validateField: validateMobileField } =
	useValidateForm(mobileForm)

async function handleOtpSend(e) {
	e.preventDefault()
	const isValidForm = validateMobileForm(true)
	if (!isValidForm) return
	state.inProgress = true
	const { data, status } = await generateOTP(mobileForm.fields.mobile)
	state.inProgress = false
	if (status === 200) {
		state.isOTPSent = true
		startTimer(() => {
			state.canResendOTP = true
		})
	} else if (status === 400) {
		mobileForm.errors = { ...data }
	}
}

const { validateForm: validateOTPForm, validateField: validateOTPField } =
	useValidateForm(otpForm)

async function handleVerifyOTP(e) {
	e.preventDefault()
	const isValidForm = validateOTPForm(true)
	if (!isValidForm) return
	state.inProgress = true
	let response
	if (!isLoggedIn.value) {
		response = await mobileLogin({
			otp: otpForm.fields.otp,
			mobile: mobileForm.fields.mobile,
		})
	} else {
		response = await verifyOTP({
			otp: otpForm.fields.otp,
			mobile: mobileForm.fields.mobile,
		})
	}
	const { data, status } = response
	state.inProgress = false
	if (status === 200) {
		store.commit('MUTATE_LOGIN', true)
		let participantInfo = data?.participant_info
		store.commit('SET_DATA_BY_KEY', participantInfo)
		moveToNextRoute(participantInfo.status)
	} else if (status === 400) {
		otpForm.errors = { ...data }
	}
}

async function resendOTP(e) {
	await handleOtpSend(e)
	state.isOTPResent = true
}

// 		onMounted(async () => {
// 			console.log()
// 		})
</script>
<style scoped>
.bg-1 {
	background: url('@/assets/bg-1.png') no-repeat 0 0 / cover;
	padding: 16px;
}
.bg-2 {
	background: url('@/assets/bg-2.png') no-repeat -6px 0 / cover;
	padding: 16px;
}
</style>
